import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Fusszeile,
  Icon,
  Kopfzeile,
  Seitenkopf,
  SkipLink,
} from '@internal/kern-ux';

/** Tools in the application header. Real links rather than buttons, so they can be opened in a new tab. */
const HeaderActions = () => (
  <>
    <RouterLink className="kern-btn kern-btn--tertiary" to="/search">
      <Icon name="search" size="small" />
      <span className="kern-label">Suche</span>
    </RouterLink>
    <RouterLink className="kern-btn kern-btn--tertiary" to="/notifications">
      <Icon name="mail" size="small" />
      <span className="kern-label">Benachrichtigungen</span>
    </RouterLink>
    <RouterLink className="kern-btn kern-btn--tertiary" to="/settings">
      <Icon name="more-vert" size="small" />
      <span className="kern-label">Einstellungen</span>
    </RouterLink>
  </>
);

/**
 * KERN page frame: identification banner, application header, navigation, content, footer.
 *
 * Replaces Backstage's `SidebarPage`. Its context is not needed because
 * `useSidebarPinState` falls back to sensible defaults without a provider.
 */
export const KernAppLayout = (props: {
  nav: ReactNode;
  content: ReactNode;
}) => {
  const config = useApi(configApiRef);
  const title = config.getOptionalString('app.title') ?? 'Backstage';
  const bannerLabel = config.getOptionalString('app.kern.banner.label');
  const showFlag = config.getOptionalBoolean('app.kern.banner.flag') ?? false;
  const operator = config.getOptionalString('app.kern.operator');
  const logoConfig = config.getOptionalConfig('app.kern.logo');
  const logo = logoConfig && {
    src: logoConfig.getString('src'),
    alt: logoConfig.getString('alt'),
  };

  return (
    <div className="vf-app">
      <SkipLink />
      {bannerLabel && <Kopfzeile label={bannerLabel} flag={showFlag} />}
      <Seitenkopf
        title={title}
        subtitle={operator}
        logo={logo}
        linkAs={RouterLink}
        actions={<HeaderActions />}
      />
      <div className="vf-app__body">
        {props.nav}
        <main id="inhalt" tabIndex={-1} className="vf-app__content">
          {props.content}
        </main>
      </div>
      <Fusszeile
        links={[
          { label: 'Barrierefreiheit', href: '/barrierefreiheit' },
          { label: 'Impressum', href: '/impressum' },
          { label: 'Datenschutz', href: '/datenschutz' },
        ]}
        note={
          <span className="vf-wordmark">
            agents<span className="vf-wordmark__accent">inflow</span>
          </span>
        }
      />
    </div>
  );
};
